import { useEffect, useState } from "react";
import { Search, SlidersHorizontal } from "lucide-react";
import {
  WorkspaceExpirationFilter,
  workspaceExpirationDays,
} from "@api/shared-runtime/workspace-expiration";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SpecialtiesSelect } from "@/components/Selects/SpecialtiesSelect";
import {
  WORKSPACE_SEARCH_DELAY_MS,
  WORKSPACE_MAX_SEARCH_LENGTH,
  type WorkspaceSearch,
} from "./workspace-search";
import type { WorkspacePool } from "./workspace-types";

const ALL_EXPIRATION_DATES = "all";
const EXPIRATION_LABELS = {
  [WorkspaceExpirationFilter.Expiring30]: "Expiring in 30 days",
  [WorkspaceExpirationFilter.Expiring60]: "Expiring in 60 days",
  [WorkspaceExpirationFilter.Expiring90]: "Expiring in 90 days",
  [WorkspaceExpirationFilter.Expired]: "Expired",
} as const;

export function WorkspaceFilters({
  search,
  pools,
  canViewPools,
  canViewCredentials,
  onChange,
}: {
  search: WorkspaceSearch;
  pools: WorkspacePool[];
  canViewPools: boolean;
  canViewCredentials: boolean;
  onChange: (changes: Partial<WorkspaceSearch>) => void;
}) {
  const [draft, setDraft] = useState(search.q ?? "");
  useEffect(() => setDraft(search.q ?? ""), [search.q]);
  useEffect(() => {
    if (draft === (search.q ?? "")) {
      return;
    }
    const timer = window.setTimeout(
      () => onChange({ q: draft.trim() || undefined }),
      WORKSPACE_SEARCH_DELAY_MS,
    );
    return () => window.clearTimeout(timer);
  }, [draft, search.q, onChange]);
  const selectedPools = search.pools ?? [];
  const hasFilters = Boolean(
    search.q ||
      selectedPools.length ||
      search.specialties?.length ||
      search.expiration ||
      (search.employment && search.employment !== "hired"),
  );

  function togglePool(poolId: string) {
    const next = selectedPools.includes(poolId)
      ? selectedPools.filter((id) => id !== poolId)
      : [...selectedPools, poolId];
    onChange({ pools: next.length ? next : undefined });
  }

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
        <div className="space-y-1.5">
          <Label htmlFor="workspace-search">Search</Label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-3 size-4 text-muted-foreground"
              aria-hidden="true"
            />
            <Input
              id="workspace-search"
              value={draft}
              maxLength={WORKSPACE_MAX_SEARCH_LENGTH}
              onChange={(event) => setDraft(event.target.value)}
              placeholder="Provider name or NPI"
              className="pl-9"
            />
          </div>
        </div>
        {canViewCredentials && (
          <div className="space-y-1.5">
            <Label htmlFor="workspace-expiration">Credential expiration</Label>
            <Select
              value={search.expiration ?? ALL_EXPIRATION_DATES}
              onValueChange={(value) =>
                onChange({
                  expiration:
                    value === ALL_EXPIRATION_DATES
                      ? undefined
                      : (value as WorkspaceExpirationFilter),
                })
              }
            >
              <SelectTrigger
                id="workspace-expiration"
                aria-describedby="workspace-expiration-context"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_EXPIRATION_DATES}>
                  All expiration dates
                </SelectItem>
                {Object.values(WorkspaceExpirationFilter).map((expiration) => (
                  <SelectItem key={expiration} value={expiration}>
                    {EXPIRATION_LABELS[expiration]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="workspace-specialty">Specialty</Label>
          <SpecialtiesSelect
            inputId="workspace-specialty"
            isMulti
            value={search.specialties ?? []}
            placeholder="All specialties"
            onChange={(value) =>
              onChange({
                specialties:
                  Array.isArray(value) && value.length ? value : undefined,
              })
            }
          />
        </div>
        {canViewPools && (
          <div className="space-y-1.5">
            <Label htmlFor="workspace-pools">Provider pools</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  id="workspace-pools"
                  variant="outline"
                  className="w-full justify-between font-normal"
                >
                  {selectedPools.length
                    ? `${selectedPools.length} selected`
                    : "All pools"}
                  <SlidersHorizontal className="size-4" aria-hidden="true" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="max-h-80 w-72 overflow-y-auto p-2"
              >
                {pools.length === 0 && (
                  <p className="p-2 text-sm text-muted-foreground">
                    No available pools.
                  </p>
                )}
                {pools.map((pool) => (
                  <label
                    key={pool.id}
                    className="flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm hover:bg-muted"
                  >
                    <Checkbox
                      checked={selectedPools.includes(pool.id)}
                      onCheckedChange={() => togglePool(pool.id)}
                    />
                    <span className="min-w-0 break-words">{pool.name}</span>
                  </label>
                ))}
              </PopoverContent>
            </Popover>
          </div>
        )}
        <div className="space-y-1.5">
          <Label htmlFor="workspace-employment">Employment status</Label>
          <Select
            value={search.employment ?? "hired"}
            onValueChange={(value) =>
              onChange({ employment: value as WorkspaceSearch["employment"] })
            }
          >
            <SelectTrigger id="workspace-employment">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="hired">Hired</SelectItem>
              <SelectItem value="candidate">Candidate</SelectItem>
              <SelectItem value="all">All providers</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {canViewCredentials && (
        <p
          id="workspace-expiration-context"
          className="text-sm text-muted-foreground"
        >
          <ExpirationContext expiration={search.expiration} /> Open a provider
          to review individual credentials.
        </p>
      )}
      {hasFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setDraft("");
            onChange({
              q: undefined,
              pools: undefined,
              specialties: undefined,
              employment: undefined,
              expiration: undefined,
            });
          }}
        >
          Reset filters
        </Button>
      )}
    </div>
  );
}

function ExpirationContext({
  expiration,
}: {
  expiration?: WorkspaceExpirationFilter;
}) {
  if (expiration === WorkspaceExpirationFilter.Expired) {
    return <>Showing providers with expired credentials.</>;
  }
  const days = workspaceExpirationDays(expiration);
  if (expiration) {
    return (
      <>
        Showing providers with credentials expiring within {days} days,
        including today.
      </>
    );
  }
  return <>Expiring counts include credentials due within {days} days.</>;
}
